import * as React from 'react';
import { observer } from 'mobx-react';
import { getUniqueElementId } from '../../../Shared/utils/form-control-utils';
import { FrequencyCode } from '../../webgiving-generated';
import { FormField } from '../form-field';
import { SvgIcon, SvgProperties } from '../svg-icon';
import { classNames } from '../../../Shared/utils/classnames';
import { Text } from '../text';

import * as styles from './frequency-button.less';

export interface IFormControlFrequencyButtonProps<T> {
	/**
	 * Current value of frequency button radio group
	 */
	field: FormField<T>;
	acceptanceTestTargetId: string;
	formControlId?: string;
	options: IFormControlFrequencyButtonOptions<T>[];
	/**
	 * The ARIA label text for the frequency button radio group.
	 * Required if ariaLabelledBy is not in use.
	 */
	ariaLabel?: string;
	/**
	 * The ARIA labelling element ID for the frequency button radio group.
	 * Required if ariaLabel is not in use.
	 */
	ariaLabelledBy?: string;
	dataFieldInvalid?: boolean;
	onChange?: (x: any) => void;
}

export interface IFormControlFrequencyButtonOptions<T> {
	value: T;
	label: string;
	icon?: SvgProperties;
}

@observer
export class FormControlFrequencyButton extends React.Component<IFormControlFrequencyButtonProps<any>, {}> {
	private fallbackElementId = getUniqueElementId();

	render() {
		const { acceptanceTestTargetId, options, ariaLabel, ariaLabelledBy } = this.props;

		const isTopRow = (match: number) => [FrequencyCode.Weekly, FrequencyCode.Fortnightly].indexOf(match) !== -1;
		const topRow = options.filter((option) => isTopRow(option.value));
		const bottomRow = options.filter((option) => !isTopRow(option.value));

		return (
			<span
				className={styles.formControlFrequencyButton}
				role="radiogroup"
				aria-label={ariaLabel}
				aria-labelledby={ariaLabelledBy}
				data-pp-at-target={acceptanceTestTargetId}
			>
				{topRow.length && this.renderRow(topRow)}
				{bottomRow.length && this.renderRow(bottomRow)}
			</span>
		);
	}

	private renderRow = (options: IFormControlFrequencyButtonOptions<any>[]) => {
		const {
			field: { value },
			acceptanceTestTargetId,
			formControlId = this.fallbackElementId,
			dataFieldInvalid = false,
		} = this.props;
		return (
			<span className={styles.formControlFrequencyButtonRow}>
				{options.map((option, index) => {
					const checked = value === option.value;
					const htmlFor = `${formControlId}_${option.value}`;

					let labelClasses = classNames(
						styles.formControlFrequencyButtonLabel,
						` brand-primary-hover brand-primary-border-hover`,
						{
							'brand-primary-bg brand-primary-border is-active': checked,
							'form-control-invalid': dataFieldInvalid,
							[styles.isActive]: checked,
						}
					);

					const icon = option.icon;

					return [
						<input
							type="radio"
							className={styles.formControlFrequencyButtonRadio}
							key={`input_${option.value}`}
							value={option.value}
							checked={checked}
							id={htmlFor}
							name={formControlId}
							onChange={this.handleChange}
							data-pp-at-target={`${acceptanceTestTargetId}_${option.label}`}
						/>,
						<label
							className={labelClasses}
							key={`label_${option.value}`}
							htmlFor={htmlFor}
							data-pp-at-target={`${acceptanceTestTargetId}_label_${option.label}`}
						>
							{icon && <SvgIcon svg={icon} />}
							<span>
								<Text>{option.label}</Text>
							</span>
						</label>,
					];
				})}
			</span>
		);
	};

	private handleChange = (event: React.FormEvent<HTMLInputElement>) => {
		const { onChange, field, options } = this.props;
		const value = parseInt(event.currentTarget.value);
		const frequency = options.filter((o) => o.value === value)[0];
		if (frequency && frequency.value) {
			field.updateValue(frequency.value);
			field.revalidate();

			if (onChange) {
				onChange(frequency.value);
			}
		}
	};
}
